import React, { useState } from 'react';
import { Envelope, Printer, X as XIcon } from 'phosphor-react';
import {
  DeliveryAddress,
  DocumentDeliverySettings,
  getDocumentMailingAddresses,
} from '@dill/dill-shared';
import { DillButton, DillInput, DillToggle } from '../Dill';
import { formatPhoneAsYouType, formatPhoneForDisplay, toE164 } from '../../../utils/phone';

// Mail and fax for a one-off Send Invoice / Send Statement. Prefilled from the customer's saved
// delivery settings for that document; changes here apply to this send only.
export type PhysicalDeliveryValue = {
  isMailEnabled: boolean;
  mailingAddresses: DeliveryAddress[];
  isFaxEnabled: boolean;
  faxNumbers: string[];
};

export const getInitialPhysicalDelivery = (
  settings?: DocumentDeliverySettings | null
): PhysicalDeliveryValue => ({
  isMailEnabled: !!settings?.isMailEnabled && !settings?.isDisabled,
  mailingAddresses: settings ? getDocumentMailingAddresses(settings) : [],
  isFaxEnabled: !!settings?.isFaxEnabled && !settings?.isDisabled,
  faxNumbers: [...new Set(settings?.faxNumbers || [])],
});

/** What to send to: only the channels that are on. */
export const toPhysicalDestinations = (value: PhysicalDeliveryValue) => ({
  mailingAddresses: value.isMailEnabled ? value.mailingAddresses : [],
  faxNumbers: value.isFaxEnabled ? value.faxNumbers : [],
});

const formatAddress = (address: DeliveryAddress) =>
  [
    address.name,
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.zipCode,
  ]
    .filter(Boolean)
    .join(', ');

const EMPTY_ADDRESS = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
};

const ChannelHeader = ({
  icon,
  title,
  value,
  onChange,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
      {icon}
      {title}
    </div>
    <DillToggle value={value} onChange={onChange} isDisabled={disabled} />
  </div>
);

const AppPhysicalDeliveryFields = ({
  value,
  onChange,
  disabled,
}: {
  value: PhysicalDeliveryValue;
  onChange: (next: PhysicalDeliveryValue) => void;
  disabled?: boolean;
}) => {
  const [newAddress, setNewAddress] = useState(EMPTY_ADDRESS);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [faxInput, setFaxInput] = useState('');
  const [faxError, setFaxError] = useState('');

  const update = (next: Partial<PhysicalDeliveryValue>) => onChange({ ...value, ...next });

  const handleAddAddress = () => {
    const address = {
      ...newAddress,
      state: newAddress.state.trim().toUpperCase(),
    };
    if (
      !address.addressLine1.trim() ||
      !address.city.trim() ||
      !address.state ||
      !address.zipCode.trim()
    ) {
      setAddressError('Enter the street, city, state and ZIP code.');
      return;
    }
    update({ mailingAddresses: [...value.mailingAddresses, address] });
    setNewAddress(EMPTY_ADDRESS);
    setIsAddingAddress(false);
    setAddressError('');
  };

  const handleAddFax = () => {
    const faxNumber = toE164(faxInput);
    if (!faxNumber) {
      setFaxError('Enter a valid fax number.');
      return;
    }
    if (value.faxNumbers.includes(faxNumber)) {
      setFaxError('This fax number is already in the list.');
      return;
    }
    update({ faxNumbers: [...value.faxNumbers, faxNumber] });
    setFaxInput('');
    setFaxError('');
  };

  const addressField = (field: keyof typeof EMPTY_ADDRESS, placeholder: string) => (
    <DillInput
      value={newAddress[field]}
      onChange={(fieldValue) => setNewAddress((prev) => ({ ...prev, [field]: fieldValue }))}
      placeholder={placeholder}
      disabled={disabled}
    />
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-gray-200 p-3 flex flex-col gap-2">
        <ChannelHeader
          icon={<Envelope size={16} weight="fill" />}
          title="Mail"
          value={value.isMailEnabled}
          onChange={(isMailEnabled) => update({ isMailEnabled })}
          disabled={disabled}
        />
        {value.isMailEnabled && (
          <>
            {value.mailingAddresses.length === 0 && (
              <p className="text-xs text-amber-700">Add a mailing address to send by mail.</p>
            )}
            {value.mailingAddresses.map((address, index) => (
              <div
                key={`${formatAddress(address)}_${index}`}
                className="flex items-start justify-between gap-2 rounded-md bg-gray-50 px-2 py-1.5">
                <span className="text-xs text-gray-700">{formatAddress(address)}</span>
                <button
                  type="button"
                  aria-label="Remove address"
                  disabled={disabled}
                  onClick={() =>
                    update({
                      mailingAddresses: value.mailingAddresses.filter((_, i) => i !== index),
                    })
                  }
                  className="bg-transparent border-none p-0 cursor-pointer text-gray-400 hover:text-gray-700">
                  <XIcon size={14} />
                </button>
              </div>
            ))}
            {isAddingAddress ? (
              <div className="flex flex-col gap-2 rounded-md border border-gray-200 p-2">
                {addressField('name', 'Name / attention (optional)')}
                {addressField('addressLine1', 'Street address')}
                {addressField('addressLine2', 'Suite, unit (optional)')}
                <div className="grid grid-cols-3 gap-2">
                  {addressField('city', 'City')}
                  {addressField('state', 'State')}
                  {addressField('zipCode', 'ZIP')}
                </div>
                {addressError && <p className="text-xs text-red-600">{addressError}</p>}
                <div className="flex justify-end gap-2">
                  <DillButton
                    label="Cancel"
                    variant="secondary"
                    height={28}
                    onClick={() => {
                      setIsAddingAddress(false);
                      setAddressError('');
                    }}
                  />
                  <DillButton label="Add address" height={28} onClick={handleAddAddress} />
                </div>
              </div>
            ) : (
              <div>
                <DillButton
                  label="Add address"
                  variant="secondary"
                  height={28}
                  onClick={() => setIsAddingAddress(true)}
                  isDisabled={disabled}
                />
              </div>
            )}
          </>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 p-3 flex flex-col gap-2">
        <ChannelHeader
          icon={<Printer size={16} />}
          title="Fax"
          value={value.isFaxEnabled}
          onChange={(isFaxEnabled) => update({ isFaxEnabled })}
          disabled={disabled}
        />
        {value.isFaxEnabled && (
          <>
            {value.faxNumbers.length === 0 && (
              <p className="text-xs text-amber-700">Add a fax number to send by fax.</p>
            )}
            {value.faxNumbers.map((faxNumber) => (
              <div
                key={faxNumber}
                className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1.5">
                <span className="text-xs text-gray-700">{formatPhoneForDisplay(faxNumber)}</span>
                <button
                  type="button"
                  aria-label="Remove fax number"
                  disabled={disabled}
                  onClick={() =>
                    update({
                      faxNumbers: value.faxNumbers.filter((number) => number !== faxNumber),
                    })
                  }
                  className="bg-transparent border-none p-0 cursor-pointer text-gray-400 hover:text-gray-700">
                  <XIcon size={14} />
                </button>
              </div>
            ))}
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <DillInput
                  value={faxInput}
                  onChange={(input) => {
                    setFaxInput(formatPhoneAsYouType(input));
                    setFaxError('');
                  }}
                  placeholder="Fax number"
                  error={faxError}
                  disabled={disabled}
                />
              </div>
              <DillButton
                label="Add"
                variant="secondary"
                height={34}
                onClick={handleAddFax}
                isDisabled={disabled || !faxInput.trim()}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AppPhysicalDeliveryFields;
